// Mock data for UCEI Media Intelligence — Unione delle Comunità Ebraiche Italiane

export type Sentiment = 'positive' | 'neutral' | 'negative'
export type Tier = 1 | 2 | 3 | 'specialized'
export type ContentType =
  | 'interview'
  | 'signed_commentary'
  | 'dedicated_article'
  | 'comment'
  | 'mention'
export type ArticleStatus = 'approved' | 'pending' | 'rejected'
export type UploadStatus = 'processing' | 'completed' | 'error'

// Antisemitism detection level for an article
export type AntisemitismLevel = 'none' | 'low' | 'medium' | 'high'

// --- Three-framework antisemitism analysis model ---
export type FrameworkLevel = 'none' | 'low' | 'medium' | 'high'
// reported = hate content reported/denounced for news purposes (not the outlet's own voice)
// endorsed = conveyed or endorsed by the article itself
// ambiguous = unclear
export type ContentRole = 'reported' | 'endorsed' | 'ambiguous'
export type JdaClassification =
  | 'antisemitic'
  | 'protected_criticism'
  | 'ambiguous'
  | 'none'

export interface FrameworkResult {
  level: FrameworkLevel
  score: number // 0–10
  matched: string[] // activated IHRA examples / JDA guidelines / code articles
  rationale: string // short explanation (Italian)
  confidence: 'high' | 'medium' | 'low'
}

export interface AntisemitismAnalysis {
  aggregate: {
    level: FrameworkLevel
    score: number
    contentRole: ContentRole
    hasDisagreement: boolean // true if the three frameworks diverge meaningfully
    confidence: 'high' | 'medium' | 'low'
  }
  ihra: FrameworkResult
  jda: FrameworkResult & { classification: JdaClassification }
  deontologico: FrameworkResult & { isJournalistConduct: boolean }
  validatedBy?: string // undefined until validated by a human
  validatedAt?: string
}

export type RassegnaSource = 'manual' | 'auto'
export type IntegrationProvider =
  | 'mimesi'
  | 'eco_stampa'
  | 'data_stampa'
  | 'email'
  | 'upload'

export interface Rassegna {
  id: string
  name: string
  date: string
  dateRange: string
  source: RassegnaSource
  provider: IntegrationProvider
  articleCount: number
  avgScore: number
  totalAVE: number
  sentimentMix: { positive: number; neutral: number; negative: number }
  // Count of articles flagged with antisemitic content in this rassegna
  antisemitismFlags: number
  status: 'processed' | 'processing' | 'error'
  articleIds: string[]
}

export interface Client {
  id: string
  name: string
  logo: string
  lastUpdate: string
  articlesThisMonth: number
  overallScore: number
  // Number of articles flagged for antisemitic content this month
  antisemitismFlags: number
  sparklineData: number[]
}

export interface Article {
  id: string
  title: string
  source: string
  sourceLogo: string
  // Optional press-clipping image (newspaper page scan) shown in the article preview
  clipping?: string
  tier: Tier
  date: string
  author: string
  contentType: ContentType
  sentiment: Sentiment
  relevanceScore: number
  ave: number
  // Antisemitism indicator (legacy single-score — derived from antisemitismAnalysis.aggregate)
  antisemitismLevel: AntisemitismLevel
  antisemitismScore: number // 0–10, higher = more severe antisemitic content detected
  // Structured three-framework analysis (IHRA / JDA / Codice deontologico).
  // Optional on the raw literals; always populated on the exported `articles`.
  antisemitismAnalysis?: AntisemitismAnalysis
  page: number
  section: string
  coverage: 'full' | 'half' | 'third' | 'quarter'
  hasPhoto: boolean
  titleMentionsClient: boolean
  boldedReference: boolean
  frontPageCallout: boolean
  inPressKit: boolean
  isAuthoritative: boolean
  status: ArticleStatus
  scoreBreakdown: {
    tier: number
    positioning: number
    contentType: number
    sentiment: number
    signature: number
  }
  excerpt: string
}

export interface Upload {
  id: string
  filename: string
  dateRange: string
  pages: number
  status: UploadStatus
  articlesExtracted: number
  progress: number
  currentStage: string
}

export const antisemitismLabels: Record<AntisemitismLevel, string> = {
  none: 'Nessuno',
  low: 'Lieve',
  medium: 'Moderato',
  high: 'Grave',
}

export const contentRoleLabels: Record<ContentRole, string> = {
  reported: 'Riportato a fini di cronaca',
  endorsed: "Veicolato dall'articolo",
  ambiguous: 'Ambiguo',
}

export const jdaClassificationLabels: Record<JdaClassification, string> = {
  antisemitic: 'Antisemita',
  protected_criticism: 'Critica legittima',
  ambiguous: 'Ambiguo',
  none: 'Nessuna criticità',
}

// Reference frameworks used by the analysis engine and the Definitions page.
// Criteria are paraphrased in Italian (NOT verbatim official text).
export const antisemitismFrameworks = {
  ihra: {
    id: 'ihra',
    title: 'IHRA — Definizione operativa di antisemitismo',
    year: 2016,
    source:
      'https://www.holocaustremembrance.com/it/risorse/definizione-operativa-antisemitismo',
    definition:
      "Definizione operativa adottata dall'International Holocaust Remembrance Alliance: l'antisemitismo è una certa percezione degli ebrei che può esprimersi come odio verso di loro, con manifestazioni retoriche e fisiche dirette agli ebrei in quanto tali, alle loro proprietà, alle istituzioni comunitarie e ai luoghi di culto. La stessa definizione precisa che la critica a Israele paragonabile a quella rivolta ad altri Stati non è di per sé antisemita.",
    note: 'La definizione include 11 esempi contemporanei; 7 riguardano Israele/sionismo e vanno applicati con cautela per non colpire la critica politica legittima.',
    criteria: [
      {
        id: 'ihra-1',
        label: 'Esempio IHRA n.1',
        text: "Incitare, sostenere o giustificare l'uccisione o il danneggiamento di ebrei in nome di un'ideologia radicale.",
      },
      {
        id: 'ihra-2',
        label: 'Esempio IHRA n.2',
        text: 'Affermazioni false, disumanizzanti o stereotipate sugli ebrei o sul loro presunto potere collettivo.',
      },
      {
        id: 'ihra-3',
        label: 'Esempio IHRA n.3',
        text: 'Accusare gli ebrei come popolo di responsabilità per atti compiuti da singoli o gruppi.',
      },
      {
        id: 'ihra-4',
        label: 'Esempio IHRA n.4',
        text: "Negare la Shoah o la portata, i meccanismi e l'intenzionalità del genocidio nazista.",
      },
      {
        id: 'ihra-5',
        label: 'Esempio IHRA n.5',
        text: 'Accusare gli ebrei o Israele di aver inventato o esagerato la Shoah.',
      },
      {
        id: 'ihra-6',
        label: 'Esempio IHRA n.6',
        text: 'Accusare i cittadini ebrei di essere più fedeli a Israele che al proprio Paese.',
      },
      {
        id: 'ihra-7',
        label: 'Esempio IHRA n.7',
        text: "Negare al popolo ebraico il diritto all'autodeterminazione, ad es. definendo Israele uno Stato intrinsecamente razzista.",
      },
      {
        id: 'ihra-8',
        label: 'Esempio IHRA n.8',
        text: 'Applicare a Israele due pesi e due misure non richiesti ad altri Stati democratici.',
      },
      {
        id: 'ihra-9',
        label: 'Esempio IHRA n.9',
        text: "Usare simboli e immagini dell'antisemitismo classico per descrivere Israele o gli israeliani.",
      },
      {
        id: 'ihra-10',
        label: 'Esempio IHRA n.10',
        text: 'Paragonare la politica israeliana contemporanea a quella nazista.',
      },
      {
        id: 'ihra-11',
        label: 'Esempio IHRA n.11',
        text: 'Ritenere gli ebrei collettivamente responsabili delle azioni dello Stato di Israele.',
      },
    ],
  },
  jda: {
    id: 'jda',
    title: 'JDA — Jerusalem Declaration on Antisemitism',
    year: 2021,
    source: 'https://jerusalemdeclaration.org',
    definition:
      "Definizione alternativa proposta da un gruppo internazionale di studiosi: l'antisemitismo è discriminazione, pregiudizio, ostilità o violenza contro gli ebrei in quanto ebrei (o le istituzioni ebraiche in quanto ebraiche). La Dichiarazione fornisce 15 linee guida pensate anche per evitare di confondere la critica politica con l'antisemitismo.",
    note: 'Le linee guida 11–15 indicano ciò che NON è di per sé antisemita: serve a ridurre i falsi positivi sulla critica legittima.',
    groups: [
      {
        id: 'general',
        title: 'Linee guida generali (1–5)',
        items: [
          {
            id: 'jda-1',
            label: 'Linea guida JDA 1',
            text: "Applicare agli ebrei stereotipi, miti o accuse di complotto classici dell'antisemitismo.",
          },
          {
            id: 'jda-2',
            label: 'Linea guida JDA 2',
            text: 'Attribuire collettivamente agli ebrei colpe o caratteristiche negative.',
          },
          {
            id: 'jda-3',
            label: 'Linea guida JDA 3',
            text: 'Negare o banalizzare la Shoah o farne un uso strumentale.',
          },
          {
            id: 'jda-4',
            label: 'Linea guida JDA 4',
            text: 'Considerare gli ebrei come agenti di un potere occulto sovranazionale.',
          },
          {
            id: 'jda-5',
            label: 'Linea guida JDA 5',
            text: 'Discriminare, escludere o aggredire persone in quanto ebree.',
          },
        ],
      },
      {
        id: 'israel-indicative',
        title: 'Israele/Palestina — indicative di antisemitismo (6–10)',
        items: [
          {
            id: 'jda-6',
            label: 'Linea guida JDA 6',
            text: "Applicare a Israele simboli e tropi dell'antisemitismo classico (es. ebreo come potere occulto).",
          },
          {
            id: 'jda-7',
            label: 'Linea guida JDA 7',
            text: 'Ritenere gli ebrei della diaspora responsabili della condotta dello Stato di Israele.',
          },
          {
            id: 'jda-8',
            label: 'Linea guida JDA 8',
            text: 'Pretendere che gli ebrei prendano pubblicamente posizione su Israele in quanto ebrei.',
          },
          {
            id: 'jda-9',
            label: 'Linea guida JDA 9',
            text: 'Negare il diritto degli ebrei israeliani a esistere e prosperare, in modo discriminatorio.',
          },
          {
            id: 'jda-10',
            label: 'Linea guida JDA 10',
            text: 'Idealizzare o giustificare la violenza contro gli ebrei in nome della causa palestinese.',
          },
        ],
      },
      {
        id: 'israel-protected',
        title: 'Israele/Palestina — ciò che NON è antisemitismo (11–15)',
        items: [
          {
            id: 'jda-11',
            label: 'Linea guida JDA 11',
            text: 'Sostenere la richiesta di pari diritti e giustizia per tutti gli abitanti tra il fiume e il mare.',
          },
          {
            id: 'jda-12',
            label: 'Linea guida JDA 12',
            text: 'Criticare o opporsi al sionismo come forma di nazionalismo.',
          },
          {
            id: 'jda-13',
            label: 'Linea guida JDA 13',
            text: 'Confronto basato su evidenze tra Israele e altri casi storici, comprese situazioni coloniali o di apartheid.',
          },
          {
            id: 'jda-14',
            label: 'Linea guida JDA 14',
            text: 'Critica, anche aspra, allo Stato di Israele, al suo governo e alle sue politiche.',
          },
          {
            id: 'jda-15',
            label: 'Linea guida JDA 15',
            text: 'Azioni politiche non violente come boicottaggi, disinvestimenti e sanzioni (BDS).',
          },
        ],
      },
    ],
  },
  deontologico: {
    id: 'deontologico',
    title: 'Codice deontologico delle giornaliste e dei giornalisti',
    year: 2025,
    source: 'https://www.odg.it',
    definition:
      "Codice unico in vigore dal 1° giugno 2025. Valuta la CONDOTTA giornalistica: linguaggio, framing e stereotipi adottati dall'autore. Riportare un episodio a fini di cronaca non costituisce violazione; lo è invece l'uso di linguaggio discriminatorio o stigmatizzante.",
    note: "La valutazione riguarda l'inquadramento dell'autore, non il tema trattato.",
    criteria: [
      {
        id: 'deon-9',
        label: 'Art. 9',
        text: 'Tutela del diritto alla non discriminazione per razza, religione, etnia, ecc.: evitare riferimenti gratuiti o stigmatizzanti.',
      },
      {
        id: 'deon-language',
        label: 'Linguaggio',
        text: 'Evitare termini, immagini o stereotipi che alimentano pregiudizio verso gli ebrei.',
      },
      {
        id: 'deon-framing',
        label: 'Inquadramento',
        text: "Distinguere chiaramente cronaca e opinione; non avallare contenuti d'odio nel riportarli.",
      },
    ],
  },
} as const

// Builds a structured three-framework analysis from the legacy single score,
// inferring contentRole from the excerpt. This keeps the 20 mock articles
// internally coherent without hand-authoring every object.
function buildAntisemitismAnalysis(
  a: Omit<Article, 'antisemitismAnalysis'>,
): AntisemitismAnalysis {
  const level = a.antisemitismLevel
  const score = a.antisemitismScore
  const text = `${a.title} ${a.excerpt}`.toLowerCase()
  const reportedSignals = [
    'riporta',
    'riportano',
    'segnalat',
    'denuncia',
    'scritte',
    'cori',
    'vandali',
    'insulti',
    'ondata',
    'osservatorio',
    'indagine',
  ]
  const ambiguousSignals = ['ambigu', 'sconcerto', 'polemica']
  const isReported =
    level !== 'none' && reportedSignals.some((s) => text.includes(s))
  const isAmbiguous =
    level !== 'none' &&
    !isReported &&
    ambiguousSignals.some((s) => text.includes(s))
  const contentRole: ContentRole =
    level === 'none'
      ? 'reported'
      : isReported
        ? 'reported'
        : isAmbiguous
          ? 'ambiguous'
          : 'endorsed'

  const confidence: FrameworkResult['confidence'] =
    level === 'none'
      ? 'high'
      : score >= 7
        ? 'high'
        : score >= 4
          ? 'medium'
          : 'low'

  // IHRA result
  const ihraMatched: string[] =
    level === 'none'
      ? []
      : score >= 7
        ? ['Esempio IHRA n.2', 'Esempio IHRA n.9']
        : ['Esempio IHRA n.2']
  const ihra: FrameworkResult = {
    level,
    score,
    matched: ihraMatched,
    confidence,
    rationale:
      level === 'none'
        ? 'Nessun elemento riconducibile agli esempi IHRA.'
        : contentRole === 'reported'
          ? "Contenuti d'odio riconducibili agli esempi IHRA, qui riportati a fini di cronaca."
          : 'Elementi riconducibili agli esempi IHRA presenti nel testo.',
  }

  // JDA result — default classification by content; political criticism would be protected.
  const jdaClassification: JdaClassification =
    level === 'none' ? 'none' : isAmbiguous ? 'ambiguous' : 'antisemitic'
  const jda: FrameworkResult & { classification: JdaClassification } = {
    level: level === 'none' ? 'none' : isAmbiguous ? 'low' : level,
    score: level === 'none' ? 0 : isAmbiguous ? Math.min(score, 4) : score,
    matched: level === 'none' ? [] : isAmbiguous ? [] : ['Linea guida JDA 1'],
    confidence,
    classification: jdaClassification,
    rationale:
      level === 'none'
        ? 'Nessuna manifestazione antisemita rilevata; eventuale critica politica considerata legittima.'
        : isAmbiguous
          ? 'Elementi non chiari: da verificare se si tratti di antisemitismo o critica legittima.'
          : 'Manifestazioni che colpiscono gli ebrei in quanto tali (linee guida JDA 1–10).',
  }

  // Deontologico — judges journalist conduct, NOT the mere fact of covering the topic.
  const isJournalistConduct =
    contentRole === 'endorsed' || contentRole === 'ambiguous'
  const deonLevel: FrameworkLevel = isJournalistConduct
    ? level
    : level === 'none'
      ? 'none'
      : 'low'
  const deontologico: FrameworkResult & { isJournalistConduct: boolean } = {
    level: deonLevel,
    score: isJournalistConduct ? score : Math.min(score, 2),
    matched: isJournalistConduct
      ? ['Art. 9', 'Linguaggio']
      : level === 'none'
        ? []
        : [],
    confidence,
    isJournalistConduct,
    rationale: isJournalistConduct
      ? "L'inquadramento o il linguaggio dell'autore potrebbe violare l'art. 9 del Codice."
      : 'Episodio riportato a fini di cronaca: nessuna violazione della condotta giornalistica.',
  }

  // Aggregate — highest framework level weighted by contentRole.
  const order: FrameworkLevel[] = ['none', 'low', 'medium', 'high']
  const maxLevel = [ihra.level, jda.level, deontologico.level].reduce(
    (m, l) => (order.indexOf(l) > order.indexOf(m) ? l : m),
    'none' as FrameworkLevel,
  )
  // If reported, the conduct dimension is reduced but the phenomenon's severity is kept.
  const aggLevel = maxLevel
  const aggScore = Math.max(ihra.score, jda.score, deontologico.score)
  const hasDisagreement =
    jda.classification === 'protected_criticism' ||
    jda.classification === 'ambiguous' ||
    Math.abs(order.indexOf(ihra.level) - order.indexOf(deontologico.level)) >= 2

  return {
    aggregate: {
      level: aggLevel,
      score: aggScore,
      contentRole,
      hasDisagreement,
      confidence,
    },
    ihra,
    jda,
    deontologico,
  }
}

// UCEI monitors media coverage of several Italian Jewish communities
export const clients: Client[] = [
  {
    id: 'ucei',
    name: 'UCEI Nazionale',
    logo: 'https://cdn.magicpatterns.com/uploads/s2MFPhMtmvBfy6H1KoBtds/Unione_Comunita_Ebraiche_Italiane_-_logo_(Italy).svg',
    lastUpdate: '2026-05-28T14:30:00',
    articlesThisMonth: 142,
    overallScore: 7.6,
    antisemitismFlags: 18,
    sparklineData: [6, 9, 5, 11, 8, 13, 7, 10, 14, 8, 12, 6, 9, 11],
  },
  {
    id: 'roma',
    name: 'Comunità Ebraica di Roma',
    logo: 'https://ui-avatars.com/api/?name=CER&background=1AA0DB&color=fff&size=64&font-size=0.35',
    lastUpdate: '2026-05-28T10:15:00',
    articlesThisMonth: 96,
    overallScore: 8.1,
    antisemitismFlags: 11,
    sparklineData: [5, 7, 4, 8, 6, 9, 5, 7, 10, 6, 8, 5, 7, 9],
  },
  {
    id: 'milano',
    name: 'Comunità Ebraica di Milano',
    logo: 'https://ui-avatars.com/api/?name=CEM&background=1AA0DB&color=fff&size=64&font-size=0.35',
    lastUpdate: '2026-05-27T18:00:00',
    articlesThisMonth: 78,
    overallScore: 7.9,
    antisemitismFlags: 7,
    sparklineData: [8, 12, 6, 9, 15, 7, 11, 8, 13, 6, 10, 14, 9, 7],
  },
]

export const sources = [
  {
    name: 'Pagine Ebraiche',
    tier: 'specialized' as Tier,
    logo: 'https://ui-avatars.com/api/?name=PE&background=1AA0DB&color=fff&size=32&font-size=0.4',
  },
  {
    name: 'Moked',
    tier: 'specialized' as Tier,
    logo: 'https://ui-avatars.com/api/?name=MK&background=0D5F8A&color=fff&size=32&font-size=0.4',
  },
  {
    name: 'Joi — Jewish Italy',
    tier: 'specialized' as Tier,
    logo: 'https://ui-avatars.com/api/?name=JOI&background=4D4D4D&color=fff&size=32&font-size=0.35',
  },
  {
    name: 'Corriere della Sera',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=CdS&background=1A2332&color=fff&size=32&font-size=0.35',
  },
  {
    name: 'la Repubblica',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=Rep&background=E30613&color=fff&size=32&font-size=0.35',
  },
  {
    name: 'La Stampa',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=LS&background=003366&color=fff&size=32&font-size=0.4',
  },
  {
    name: 'Il Sole 24 Ore',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=S24&background=F5A623&color=111&size=32&font-size=0.4',
  },
  {
    name: 'Avvenire',
    tier: 2 as Tier,
    logo: 'https://ui-avatars.com/api/?name=Avv&background=8A1538&color=fff&size=32&font-size=0.35',
  },
  {
    name: 'Il Foglio',
    tier: 2 as Tier,
    logo: 'https://ui-avatars.com/api/?name=Fgl&background=2E2E2E&color=fff&size=32&font-size=0.35',
  },
  {
    name: 'Il Messaggero',
    tier: 2 as Tier,
    logo: 'https://ui-avatars.com/api/?name=IM&background=0055A4&color=fff&size=32&font-size=0.4',
  },
]

export const contentTypeLabels: Record<ContentType, string> = {
  interview: 'Intervista',
  signed_commentary: 'Commento firmato',
  dedicated_article: 'Articolo dedicato',
  comment: 'Commento',
  mention: 'Citazione',
}

export const sentimentLabels: Record<Sentiment, string> = {
  positive: 'Positivo',
  neutral: 'Neutro',
  negative: 'Negativo',
}

export const tierLabels: Record<string, string> = {
  '1': 'Tier 1',
  '2': 'Tier 2',
  '3': 'Tier 3',
  specialized: 'Specializzata',
}

const rawArticles: Article[] = [
  {
    id: '1',
    title:
      'Giorno della Memoria, Noemi Di Segni: «La testimonianza è un dovere che ci unisce»',
    source: 'Pagine Ebraiche',
    sourceLogo: sources[0].logo,
    clipping:
      'https://cdn.magicpatterns.com/uploads/ikYDMHogQ5TKN5AdJuJHh7/IMG_0003.jpg',
    tier: 'specialized',
    date: '2026-05-28',
    author: 'Daniel Reichel',
    contentType: 'interview',
    sentiment: 'positive',
    relevanceScore: 9.5,
    ave: 18000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 1,
    section: 'Primo Piano',
    coverage: 'full',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: true,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 2.5,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 1.0,
    },
    excerpt:
      "In vista delle celebrazioni per il Giorno della Memoria, la presidente dell'UCEI Noemi Di Segni sottolinea il valore della testimonianza e dell'educazione contro l'odio. «Ricordare la Shoah significa difendere oggi i valori della convivenza e della dignità di ogni persona»...",
  },
  {
    id: '2',
    title:
      'Comunità ebraiche, al via il progetto nazionale per i beni culturali e le sinagoghe storiche',
    source: 'Moked',
    sourceLogo: sources[1].logo,
    clipping:
      'https://cdn.magicpatterns.com/uploads/bxZusPzb8iMgxXqPBxjz3V/IMG_0001.png',
    tier: 'specialized',
    date: '2026-05-27',
    author: 'Ada Treves',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 8.2,
    ave: 12000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 2,
    section: 'Cultura',
    coverage: 'half',
    hasPhoto: false,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 1.2,
      signature: 1.0,
    },
    excerpt:
      "Parte il piano dell'UCEI per la valorizzazione delle sinagoghe storiche e degli archivi delle Comunità ebraiche italiane, con il sostegno dei fondi Otto per Mille e la collaborazione del Ministero della Cultura...",
  },
  {
    id: '3',
    title:
      'Antisemitismo online, allarme dopo l’ondata di insulti sui social nelle ultime settimane',
    source: 'Corriere della Sera',
    sourceLogo: sources[3].logo,
    clipping:
      'https://cdn.magicpatterns.com/uploads/uffVFT6KSNGkTNMJS45KZp/IMG_0007.jpg',
    tier: 1,
    date: '2026-05-27',
    author: 'Goffredo Buccini',
    contentType: 'signed_commentary',
    sentiment: 'negative',
    relevanceScore: 6.4,
    ave: 32000,
    antisemitismLevel: 'high',
    antisemitismScore: 8.5,
    page: 9,
    section: 'Cronache',
    coverage: 'third',
    hasPhoto: true,
    titleMentionsClient: false,
    boldedReference: false,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.0,
      contentType: 1.5,
      sentiment: 0.5,
      signature: 0.9,
    },
    excerpt:
      "L'Osservatorio antisemitismo registra un'impennata di contenuti d'odio sui social network. L'articolo riporta e analizza numerosi messaggi antisemiti diffusi online, segnalati anche dalle Comunità ebraiche, e si interroga sulle responsabilità delle piattaforme...",
  },
  {
    id: '4',
    title:
      'Festival della cultura ebraica, migliaia di visitatori nelle città italiane',
    source: 'Joi — Jewish Italy',
    sourceLogo: sources[2].logo,
    clipping:
      'https://cdn.magicpatterns.com/uploads/gCKrKRu3o5NYa3vhDrM3Av/IMG_0009.jpg',
    tier: 'specialized',
    date: '2026-05-26',
    author: 'Sofia Tranchina',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 7.8,
    ave: 6500,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 1,
    section: 'Eventi',
    coverage: 'full',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: true,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 2.0,
      contentType: 2.0,
      sentiment: 1.0,
      signature: 0.8,
    },
    excerpt:
      'Grande successo per la Giornata Europea della Cultura Ebraica: visite guidate alle sinagoghe, concerti, laboratori per le scuole e incontri promossi dalle Comunità ebraiche hanno coinvolto un pubblico numeroso e trasversale...',
  },
  {
    id: '5',
    title: 'Scuole ebraiche, nuovo protocollo sulla sicurezza con il Ministero',
    source: 'La Stampa',
    sourceLogo: sources[5].logo,
    clipping:
      'https://cdn.magicpatterns.com/uploads/dQENDz22CWxW3WvjBQWjk9/IMG_0005.jpg',
    tier: 1,
    date: '2026-05-25',
    author: 'Francesca Sforza',
    contentType: 'comment',
    sentiment: 'neutral',
    relevanceScore: 5.5,
    ave: 24000,
    antisemitismLevel: 'low',
    antisemitismScore: 2.5,
    page: 12,
    section: 'Cronaca',
    coverage: 'quarter',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 0.5,
      contentType: 1.0,
      sentiment: 1.0,
      signature: 0.5,
    },
    excerpt:
      "Firmato il nuovo protocollo per la sicurezza delle scuole e dei luoghi di culto ebraici. L'intesa, sollecitata dalle Comunità dopo i recenti episodi di tensione, prevede maggiore sorveglianza e percorsi di prevenzione dell'odio...",
  },
  {
    id: '6',
    title:
      'Memoria e dialogo interreligioso, l’incontro tra le confessioni a Roma',
    source: 'la Repubblica',
    sourceLogo: sources[4].logo,
    clipping:
      'https://cdn.magicpatterns.com/uploads/o1Rujyc3ZCE1ui8zoPAZhg/IMG_0006.jpg',
    tier: 1,
    date: '2026-05-24',
    author: 'Paolo Rodari',
    contentType: 'mention',
    sentiment: 'positive',
    relevanceScore: 6.0,
    ave: 22000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 15,
    section: 'Società',
    coverage: 'third',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: false,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'pending',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.0,
      contentType: 0.5,
      sentiment: 1.0,
      signature: 1.0,
    },
    excerpt:
      "Un incontro tra rappresentanti ebraici, cristiani e musulmani ha rilanciato il dialogo interreligioso nella capitale. Presente una delegazione dell'UCEI, che ha ribadito l'impegno comune contro ogni forma di discriminazione...",
  },
  {
    id: '7',
    title:
      'Otto per Mille, le Comunità ebraiche presentano i progetti sociali finanziati',
    source: 'Avvenire',
    sourceLogo: sources[7].logo,
    tier: 2,
    date: '2026-05-23',
    author: 'Riccardo Maccioni',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 8.5,
    ave: 14000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 18,
    section: 'Solidarietà',
    coverage: 'half',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 2.0,
      contentType: 2.0,
      sentiment: 2.0,
      signature: 1.0,
    },
    excerpt:
      "L'UCEI ha illustrato i progetti sociali e culturali sostenuti grazie ai fondi dell'Otto per Mille: assistenza agli anziani, sostegno alle scuole e iniziative di memoria, con un'attenzione particolare ai più fragili...",
  },
  {
    id: '8',
    title:
      'Educazione contro l’odio: il piano UCEI per le scuole superiori entra nel vivo',
    source: 'Il Foglio',
    sourceLogo: sources[8].logo,
    tier: 2,
    date: '2026-05-22',
    author: 'Giulio Meotti',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 7.2,
    ave: 12500,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 14,
    section: 'Educazione',
    coverage: 'third',
    hasPhoto: false,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 0.7,
    },
    excerpt:
      "Prosegue il programma educativo promosso dall'UCEI nelle scuole superiori per contrastare i pregiudizi e l'antisemitismo. Testimonianze, percorsi didattici e visite ai luoghi della memoria coinvolgono migliaia di studenti...",
  },
  {
    id: '9',
    title:
      'Scritte antisemite su un muro nel centro storico: la condanna delle istituzioni',
    source: 'Il Messaggero',
    sourceLogo: sources[9].logo,
    tier: 2,
    date: '2026-05-21',
    author: 'Rosario Dimito',
    contentType: 'comment',
    sentiment: 'negative',
    relevanceScore: 6.8,
    ave: 14000,
    antisemitismLevel: 'high',
    antisemitismScore: 7.8,
    page: 11,
    section: 'Cronaca',
    coverage: 'half',
    hasPhoto: true,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.5,
      contentType: 1.5,
      sentiment: 1.0,
      signature: 1.3,
    },
    excerpt:
      "Scoperte all'alba alcune scritte antisemite e simboli d'odio nel centro storico. Ferma la condanna del sindaco e della Comunità ebraica locale; le forze dell'ordine indagano. L'articolo riporta il contenuto delle scritte ai fini di cronaca...",
  },
  {
    id: '10',
    title:
      "Intervista al rabbino capo: 'Le nostre Comunità sono un patrimonio vivo dell'Italia'",
    source: 'Pagine Ebraiche',
    sourceLogo: sources[0].logo,
    tier: 'specialized',
    date: '2026-05-20',
    author: 'Rossella Tercatin',
    contentType: 'interview',
    sentiment: 'positive',
    relevanceScore: 9.8,
    ave: 16000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 2,
    section: 'Primo Piano',
    coverage: 'full',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: true,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 2.5,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 1.3,
    },
    excerpt:
      "In una lunga intervista, il rabbino capo riflette sul ruolo delle Comunità ebraiche nella società italiana: 'Siamo una presenza antica e viva, custodi di una tradizione che arricchisce l'intero Paese. Il dialogo è la nostra strada'...",
  },
  {
    id: '11',
    title:
      'Beni culturali ebraici, dalla Toscana al Sud cresce la rete dei musei e degli itinerari',
    source: 'Il Sole 24 Ore',
    sourceLogo: sources[6].logo,
    tier: 1,
    date: '2026-05-19',
    author: 'Marilena Pirrelli',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 8.0,
    ave: 30000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 7,
    section: 'Cultura',
    coverage: 'half',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 1.0,
      signature: 1.0,
    },
    excerpt:
      "Si rafforza la rete dei musei ebraici e degli itinerari culturali, dai ghetti storici alle sinagoghe del Mezzogiorno. Un patrimonio che attrae turismo e che le Comunità, con l'UCEI, lavorano a tutelare e raccontare...",
  },
  {
    id: '12',
    title:
      "Sicurezza dei luoghi di culto, l'UCEI: 'Monitoraggio costante con le istituzioni'",
    source: 'Moked',
    sourceLogo: sources[1].logo,
    tier: 'specialized',
    date: '2026-05-18',
    author: 'Daniel Reichel',
    contentType: 'mention',
    sentiment: 'neutral',
    relevanceScore: 7.5,
    ave: 9000,
    antisemitismLevel: 'low',
    antisemitismScore: 2.0,
    page: 3,
    section: 'Attualità',
    coverage: 'third',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 1.5,
      contentType: 1.0,
      sentiment: 1.0,
      signature: 1.5,
    },
    excerpt:
      "L'UCEI conferma il monitoraggio costante sulla sicurezza di sinagoghe, scuole e centri comunitari, in raccordo con il Ministero dell'Interno, dopo le segnalazioni di tensioni e di episodi d'odio nelle scorse settimane...",
  },
  {
    id: '13',
    title:
      'Negazionismo, polemica per le frasi di un opinionista in un talk show televisivo',
    source: 'Corriere della Sera',
    sourceLogo: sources[3].logo,
    tier: 1,
    date: '2026-05-17',
    author: 'Aldo Cazzullo',
    contentType: 'signed_commentary',
    sentiment: 'negative',
    relevanceScore: 4.2,
    ave: 38000,
    antisemitismLevel: 'medium',
    antisemitismScore: 5.5,
    page: 22,
    section: 'Cultura',
    coverage: 'half',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 0.5,
      contentType: 1.0,
      sentiment: -1.0,
      signature: 1.2,
    },
    excerpt:
      "Suscitano dure reazioni le affermazioni di un opinionista durante una trasmissione televisiva, giudicate ambigue rispetto alla memoria della Shoah. L'UCEI esprime sconcerto e chiede maggiore responsabilità ai media...",
  },
  {
    id: '14',
    title: 'Pubblicato il nuovo volume sulla storia degli ebrei italiani',
    source: 'Joi — Jewish Italy',
    sourceLogo: sources[2].logo,
    tier: 'specialized',
    date: '2026-05-16',
    author: 'Sofia Tranchina',
    contentType: 'dedicated_article',
    sentiment: 'neutral',
    relevanceScore: 6.5,
    ave: 5000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 4,
    section: 'Libri',
    coverage: 'third',
    hasPhoto: false,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 0.5,
      signature: 0.5,
    },
    excerpt:
      "Esce un'opera che ricostruisce secoli di presenza ebraica in Italia, dalle origini ai giorni nostri. Un lavoro corale che intreccia storia, cultura e memoria delle Comunità del Paese...",
  },
  {
    id: '15',
    title:
      'Cori da stadio offensivi, indagine dopo le segnalazioni di antisemitismo',
    source: 'La Stampa',
    sourceLogo: sources[5].logo,
    tier: 1,
    date: '2026-05-16',
    author: 'Paolo Baroni',
    contentType: 'mention',
    sentiment: 'negative',
    relevanceScore: 5.0,
    ave: 26000,
    antisemitismLevel: 'high',
    antisemitismScore: 8.0,
    page: 14,
    section: 'Sport',
    coverage: 'quarter',
    hasPhoto: true,
    titleMentionsClient: false,
    boldedReference: false,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: false,
    status: 'pending',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.0,
      contentType: 0.5,
      sentiment: 0.5,
      signature: 0.5,
    },
    excerpt:
      'Indagine della Procura federale dopo le segnalazioni di cori antisemiti durante una partita. Dura la presa di posizione delle Comunità ebraiche, che chiedono sanzioni esemplari e percorsi educativi nelle tifoserie...',
  },
  {
    id: '16',
    title:
      'Restauro del cimitero ebraico: al via i lavori grazie ai fondi pubblici',
    source: 'la Repubblica',
    sourceLogo: sources[4].logo,
    tier: 1,
    date: '2026-05-15',
    author: 'Corrado Zunino',
    contentType: 'comment',
    sentiment: 'positive',
    relevanceScore: 5.8,
    ave: 21000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 8,
    section: 'Cultura',
    coverage: 'half',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.5,
      contentType: 1.0,
      sentiment: 1.0,
      signature: 1.3,
    },
    excerpt:
      "Partono i lavori di restauro dell'antico cimitero ebraico, gioiello storico finora a rischio degrado. Il progetto, sostenuto da fondi pubblici e dalla Comunità, restituirà alla città un luogo simbolo della memoria...",
  },
  {
    id: '17',
    title:
      'Le Comunità ebraiche rafforzano i percorsi di formazione per i giovani',
    source: 'Avvenire',
    sourceLogo: sources[7].logo,
    tier: 2,
    date: '2026-05-15',
    author: 'Lucia Bellaspiga',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 7.0,
    ave: 11000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 20,
    section: 'Giovani',
    coverage: 'third',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 0.5,
    },
    excerpt:
      'Nuove iniziative di formazione e leadership giovanile promosse dalle Comunità ebraiche, con campi estivi, scambi internazionali e percorsi di studio della tradizione e della lingua ebraica...',
  },
  {
    id: '18',
    title:
      'Memoria della Shoah, presentato il calendario delle iniziative nazionali',
    source: 'Il Foglio',
    sourceLogo: sources[8].logo,
    tier: 2,
    date: '2026-05-14',
    author: 'Annalena Benini',
    contentType: 'comment',
    sentiment: 'neutral',
    relevanceScore: 6.2,
    ave: 13500,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 10,
    section: 'Cultura',
    coverage: 'quarter',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: false,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.0,
      contentType: 1.5,
      sentiment: 1.0,
      signature: 1.2,
    },
    excerpt:
      "Presentato il calendario delle iniziative per la memoria della Shoah: mostre, spettacoli, incontri nelle scuole e cerimonie istituzionali coordinate con l'UCEI e le Comunità del territorio...",
  },
  {
    id: '19',
    title: 'Turismo delle radici, crescono gli itinerari nei quartieri ebraici',
    source: 'Il Messaggero',
    sourceLogo: sources[9].logo,
    tier: 2,
    date: '2026-05-14',
    author: 'Umberto Mancini',
    contentType: 'mention',
    sentiment: 'neutral',
    relevanceScore: 5.5,
    ave: 10500,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 15,
    section: 'Turismo',
    coverage: 'quarter',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: false,
    status: 'pending',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.0,
      contentType: 1.0,
      sentiment: 1.0,
      signature: 1.0,
    },
    excerpt:
      'Aumentano i visitatori interessati al patrimonio ebraico italiano: ghetti, sinagoghe e musei diventano mete del turismo culturale, con ricadute positive sulle città e nuove guide formate dalle Comunità...',
  },
  {
    id: '20',
    title:
      'Dialogo Italia–Israele, convegno sulla cooperazione culturale e scientifica',
    source: 'Pagine Ebraiche',
    sourceLogo: sources[0].logo,
    tier: 'specialized',
    date: '2026-05-14',
    author: 'Rossella Tercatin',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 7.6,
    ave: 9000,
    antisemitismLevel: 'none',
    antisemitismScore: 0,
    page: 5,
    section: 'Internazionale',
    coverage: 'third',
    hasPhoto: false,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 1.0,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 0.6,
    },
    excerpt:
      "Un convegno ha messo a fuoco la cooperazione culturale e scientifica tra Italia e Israele. Presente l'UCEI, che ha richiamato il valore dei ponti tra società civili e del contrasto a ogni forma di pregiudizio...",
  },
]

// Exported articles always carry the structured three-framework analysis.
// Legacy antisemitismLevel/antisemitismScore are kept in sync with aggregate.
export const articles: Article[] = rawArticles.map((a) => {
  const antisemitismAnalysis =
    a.antisemitismAnalysis ?? buildAntisemitismAnalysis(a)
  return {
    ...a,
    antisemitismAnalysis,
    antisemitismLevel: antisemitismAnalysis.aggregate.level,
    antisemitismScore: antisemitismAnalysis.aggregate.score,
  }
})

export const uploads: Upload[] = [
  {
    id: '1',
    filename: 'Rassegna_Stampa_UCEI_28mag2026.pdf',
    dateRange: '28 Mag 2026',
    pages: 24,
    status: 'completed',
    articlesExtracted: 8,
    progress: 100,
    currentStage: 'Completato',
  },
  {
    id: '2',
    filename: 'Rassegna_Stampa_UCEI_27mag2026.pdf',
    dateRange: '27 Mag 2026',
    pages: 18,
    status: 'completed',
    articlesExtracted: 5,
    progress: 100,
    currentStage: 'Completato',
  },
  {
    id: '3',
    filename: 'Rassegna_Stampa_UCEI_26mag2026.pdf',
    dateRange: '26 Mag 2026',
    pages: 32,
    status: 'processing',
    articlesExtracted: 4,
    progress: 65,
    currentStage: 'Analisi contenuto, sentiment e antisemitismo...',
  },
  {
    id: '4',
    filename: 'Eco_della_Stampa_Maggio_W3.pdf',
    dateRange: '21-27 Mag 2026',
    pages: 56,
    status: 'processing',
    articlesExtracted: 0,
    progress: 25,
    currentStage: 'Estrazione ritagli...',
  },
  {
    id: '5',
    filename: 'DataStampa_Export_Apr2026.pdf',
    dateRange: '1-30 Apr 2026',
    pages: 128,
    status: 'error',
    articlesExtracted: 0,
    progress: 12,
    currentStage: 'Errore: formato PDF non supportato',
  },
]

// Calculated to match the 20 articles above
export const dashboardKPIs = {
  totalArticles: { value: 20, trend: +15, previousPeriod: 17 },
  avgRelevanceScore: { value: 6.9, distribution: [1, 3, 5, 6, 3, 2, 0] },
  totalAVE: { value: 364500, trend: +18, previousPeriod: 309000 },
  sentimentMix: { positive: 10, neutral: 6, negative: 4 },
  // Antisemitism indicator: number of articles flagged + average severity of flagged articles
  antisemitism: { flagged: 5, trend: +25, previousPeriod: 4, avgSeverity: 6.4 },
}

export const coverageByTier = [
  { tier: 'Specializzata', count: 6, percentage: 30 },
  { tier: 'Tier 1', count: 8, percentage: 40 },
  { tier: 'Tier 2', count: 6, percentage: 30 },
  { tier: 'Tier 3', count: 0, percentage: 0 },
]

export const sentimentOverTime = [
  { date: '14 Mag', positive: 2, neutral: 2, negative: 1 },
  { date: '15 Mag', positive: 1, neutral: 0, negative: 1 },
  { date: '16 Mag', positive: 0, neutral: 1, negative: 1 },
  { date: '17 Mag', positive: 0, neutral: 0, negative: 1 },
  { date: '18 Mag', positive: 0, neutral: 1, negative: 0 },
  { date: '19 Mag', positive: 1, neutral: 0, negative: 0 },
  { date: '20 Mag', positive: 1, neutral: 0, negative: 0 },
  { date: '21 Mag', positive: 0, neutral: 0, negative: 1 },
  { date: '22 Mag', positive: 1, neutral: 0, negative: 0 },
  { date: '23 Mag', positive: 1, neutral: 0, negative: 0 },
  { date: '24 Mag', positive: 1, neutral: 0, negative: 0 },
  { date: '25 Mag', positive: 0, neutral: 1, negative: 0 },
  { date: '26 Mag', positive: 1, neutral: 0, negative: 0 },
  { date: '27 Mag', positive: 1, neutral: 0, negative: 1 },
  { date: '28 Mag', positive: 1, neutral: 0, negative: 0 },
]

// Antisemitic content detected over time (flagged articles per day)
export const antisemitismOverTime = [
  { date: '14 Mag', flagged: 0 },
  { date: '15 Mag', flagged: 1 },
  { date: '16 Mag', flagged: 1 },
  { date: '17 Mag', flagged: 1 },
  { date: '18 Mag', flagged: 1 },
  { date: '19 Mag', flagged: 0 },
  { date: '20 Mag', flagged: 0 },
  { date: '21 Mag', flagged: 1 },
  { date: '22 Mag', flagged: 0 },
  { date: '23 Mag', flagged: 0 },
  { date: '24 Mag', flagged: 0 },
  { date: '25 Mag', flagged: 1 },
  { date: '26 Mag', flagged: 0 },
  { date: '27 Mag', flagged: 1 },
  { date: '28 Mag', flagged: 0 },
]

export const topSources = [
  { name: 'Pagine Ebraiche', articles: 3, avgScore: 8.9 },
  { name: 'Moked', articles: 2, avgScore: 7.9 },
  { name: 'Corriere della Sera', articles: 2, avgScore: 5.3 },
  { name: 'La Stampa', articles: 2, avgScore: 5.2 },
  { name: 'la Repubblica', articles: 2, avgScore: 5.9 },
]

export const spokespersonMentions = [
  {
    name: 'Noemi Di Segni',
    role: 'Presidente UCEI',
    mentions: 14,
    avgSentiment: 'positive' as Sentiment,
  },
  {
    name: 'Rav Riccardo Di Segni',
    role: 'Rabbino Capo di Roma',
    mentions: 8,
    avgSentiment: 'positive' as Sentiment,
  },
  {
    name: 'Victor Fadlun',
    role: 'Presidente Comunità Ebraica di Roma',
    mentions: 5,
    avgSentiment: 'neutral' as Sentiment,
  },
  {
    name: 'Walker Meghnagi',
    role: 'Presidente Comunità Ebraica di Milano',
    mentions: 3,
    avgSentiment: 'positive' as Sentiment,
  },
]

export const weeklySparklines = [
  { week: 'W4 Mag', data: [3, 5, 2, 7, 4, 6, 3] },
  { week: 'W3 Mag', data: [8, 5, 4, 6, 3, 5, 7] },
  { week: 'W2 Mag', data: [4, 6, 3, 5, 7, 4, 6] },
  { week: 'W1 Mag', data: [5, 3, 7, 4, 6, 8, 5] },
]

export const executiveSummary = `Nel periodo analizzato (14-28 Maggio 2026), la copertura mediatica relativa all'UCEI e alle Comunità ebraiche italiane è stata ampia e di alto profilo, trainata dalle iniziative per la memoria della Shoah, dalla Giornata Europea della Cultura Ebraica e dai progetti sostenuti con l'Otto per Mille. La visibilità sulle testate specializzate (Pagine Ebraiche, Moked, Joi) e Tier 1 (Corriere, Repubblica, Il Sole 24 Ore) è stata eccellente, con un sentiment prevalentemente positivo o neutro. Sul fronte del monitoraggio dell'antisemitismo, 5 articoli sono stati segnalati per contenuti d'odio — in crescita rispetto al periodo precedente — riconducibili principalmente a episodi online, cori da stadio, scritte vandaliche e dichiarazioni pubbliche ambigue sulla memoria. Tali contenuti, pur riportati a fini di cronaca o di denuncia, richiedono attenzione costante e un presidio educativo continuo.`

export const rassegne: Rassegna[] = [
  {
    id: 'r1',
    name: 'Rassegna Mimesi — 28 Maggio 2026',
    date: '2026-05-28',
    dateRange: '28 Mag 2026',
    source: 'auto',
    provider: 'mimesi',
    articleCount: 1,
    avgScore: 9.5,
    totalAVE: 18000,
    sentimentMix: { positive: 1, neutral: 0, negative: 0 },
    antisemitismFlags: 0,
    status: 'processed',
    articleIds: ['1'],
  },
  {
    id: 'r2',
    name: 'Rassegna Eco della Stampa — 27 Maggio 2026',
    date: '2026-05-27',
    dateRange: '27 Mag 2026',
    source: 'auto',
    provider: 'eco_stampa',
    articleCount: 2,
    avgScore: 7.3,
    totalAVE: 44000,
    sentimentMix: { positive: 1, neutral: 0, negative: 1 },
    antisemitismFlags: 1,
    status: 'processed',
    articleIds: ['2', '3'],
  },
  {
    id: 'r3',
    name: 'Rassegna Data Stampa — 26 Maggio 2026',
    date: '2026-05-26',
    dateRange: '26 Mag 2026',
    source: 'auto',
    provider: 'data_stampa',
    articleCount: 1,
    avgScore: 7.8,
    totalAVE: 6500,
    sentimentMix: { positive: 1, neutral: 0, negative: 0 },
    antisemitismFlags: 0,
    status: 'processing',
    articleIds: ['4'],
  },
  {
    id: 'r4',
    name: 'Rassegna caricata manualmente — 23-25 Maggio',
    date: '2026-05-25',
    dateRange: '23-25 Mag 2026',
    source: 'manual',
    provider: 'upload',
    articleCount: 3,
    avgScore: 6.6,
    totalAVE: 60000,
    sentimentMix: { positive: 2, neutral: 1, negative: 0 },
    antisemitismFlags: 1,
    status: 'processed',
    articleIds: ['5', '6', '7'],
  },
  {
    id: 'r5',
    name: 'Rassegna Email — Settimana 21',
    date: '2026-05-20',
    dateRange: '20-22 Mag 2026',
    source: 'auto',
    provider: 'email',
    articleCount: 3,
    avgScore: 7.9,
    totalAVE: 37500,
    sentimentMix: { positive: 2, neutral: 1, negative: 0 },
    antisemitismFlags: 0,
    status: 'processed',
    articleIds: ['8', '9', '10'],
  },
  {
    id: 'r6',
    name: 'Rassegna Mimesi — 14-19 Maggio 2026',
    date: '2026-05-19',
    dateRange: '14-19 Mag 2026',
    source: 'auto',
    provider: 'mimesi',
    articleCount: 10,
    avgScore: 6.3,
    totalAVE: 198500,
    sentimentMix: { positive: 5, neutral: 3, negative: 2 },
    antisemitismFlags: 3,
    status: 'processed',
    articleIds: ['11', '12', '13', '14', '15', '16', '17', '18', '19', '20'],
  },
]

export interface Report {
  id: string
  name: string
  dateRange: string
  template: 'executive' | 'standard' | 'full'
  status: 'draft' | 'completed'
  createdAt: string
  articleCount: number
  sections: string[]
}

export const reports: Report[] = [
  {
    id: 'rep1',
    name: 'Report Mensile Maggio 2026',
    dateRange: '1-31 Mag 2026',
    template: 'standard',
    status: 'completed',
    createdAt: '2026-05-31T10:00:00',
    articleCount: 45,
    sections: ['summary', 'kpi', 'top_articles', 'sentiment', 'tier'],
  },
  {
    id: 'rep2',
    name: 'Report Antisemitismo Q1 2026',
    dateRange: '1 Gen - 31 Mar 2026',
    template: 'executive',
    status: 'completed',
    createdAt: '2026-04-05T14:30:00',
    articleCount: 120,
    sections: ['summary', 'kpi', 'top_articles'],
  },
  {
    id: 'rep3',
    name: 'Analisi Copertura Giorno della Memoria',
    dateRange: '20-31 Gen 2026',
    template: 'full',
    status: 'completed',
    createdAt: '2026-02-03T09:15:00',
    articleCount: 32,
    sections: [
      'summary',
      'kpi',
      'top_articles',
      'sentiment',
      'tier',
      'sources',
      'spokespersons',
      'all_articles',
    ],
  },
  {
    id: 'rep4',
    name: 'Bozza Report Settimanale',
    dateRange: '1-7 Giu 2026',
    template: 'standard',
    status: 'draft',
    createdAt: '2026-06-01T11:00:00',
    articleCount: 12,
    sections: ['summary', 'kpi', 'top_articles', 'sentiment'],
  },
  {
    id: 'rep5',
    name: 'Report Speciale Cultura Ebraica',
    dateRange: '1 Gen - 31 Mag 2026',
    template: 'full',
    status: 'completed',
    createdAt: '2026-06-02T16:45:00',
    articleCount: 28,
    sections: [
      'summary',
      'kpi',
      'top_articles',
      'sentiment',
      'tier',
      'sources',
      'all_articles',
    ],
  },
]

export interface Connector {
  id: IntegrationProvider
  name: string
  description: string
  status: 'connected' | 'disconnected' | 'error'
  lastSync?: string
  rassegneIngested: number
  config: Record<string, string>
}

export const connectors: Connector[] = [
  {
    id: 'mimesi',
    name: 'Mimesi',
    description: 'Ingestione automatica via email dalla piattaforma Mimesi',
    status: 'connected',
    lastSync: '2026-05-28T08:00:00',
    rassegneIngested: 42,
    config: { email: 'rassegne@ucei.it', folder: 'INBOX/Mimesi' },
  },
  {
    id: 'eco_stampa',
    name: "L'Eco della Stampa",
    description:
      'Ingestione automatica via FTP dalla piattaforma Eco della Stampa',
    status: 'connected',
    lastSync: '2026-05-28T07:30:00',
    rassegneIngested: 28,
    config: { host: 'ftp.ecostampa.it', user: 'ucei' },
  },
  {
    id: 'data_stampa',
    name: 'Data Stampa',
    description: 'Ingestione automatica via API REST',
    status: 'error',
    lastSync: '2026-05-27T06:00:00',
    rassegneIngested: 15,
    config: { apiKey: '•••••••••••••••' },
  },
  {
    id: 'email',
    name: 'Email generica',
    description:
      'Monitoraggio di una casella email dedicata per PDF in allegato',
    status: 'disconnected',
    rassegneIngested: 0,
    config: {},
  },
]

export interface AlgorithmWeights {
  tier1: number
  tier2: number
  tier3: number
  tierSpecialized: number
  positioningFrontPage: number
  positioningPhoto: number
  positioningBold: number
  positioningTitleMention: number
  contentInterview: number
  contentSignedCommentary: number
  contentDedicatedArticle: number
  contentComment: number
  contentMention: number
  sentimentPositive: number
  sentimentNeutral: number
  sentimentNegative: number
  authoritativeSignature: number
}

export const defaultWeights: AlgorithmWeights = {
  tier1: 2.5,
  tier2: 1.5,
  tier3: 0.5,
  tierSpecialized: 2.0,
  positioningFrontPage: 2.5,
  positioningPhoto: 0.5,
  positioningBold: 1.0,
  positioningTitleMention: 1.5,
  contentInterview: 2.0,
  contentSignedCommentary: 1.8,
  contentDedicatedArticle: 2.0,
  contentComment: 1.0,
  contentMention: 0.5,
  sentimentPositive: 1.5,
  sentimentNeutral: 1.0,
  sentimentNegative: 0.5,
  authoritativeSignature: 1.5,
}
